import { typography, radius, semantic } from '@/shared/theme/tokens';
import { withAlpha } from '../../liveModeMeta';

/**
 * Top of the Recommended screen — the Consensus header pattern, theme-aware.
 * Two bands: identity (LIVE/DEMO + plant + freshness + clock) and the adaptive
 * headline banner (the single most important readout). Light + dark via
 * useThemeColors `c`; the headline surface is derived from its own status colour.
 */
export function Header({ deploymentName, clock, jitterActive, animate, headline, freshness, c, isDark }) {
  const time = clock.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', second: '2-digit' });
  const date = clock.toLocaleDateString(undefined, { weekday: 'short', day: '2-digit', month: 'short' });
  const liveColor = jitterActive ? semantic.successAlt : semantic.error;

  return (
    <div style={{
      display: 'flex', flexDirection: 'column', gap: 10, padding: '10px 16px 12px',
      borderRadius: radius.xl, background: c.bg, border: `1px solid ${c.border}`,
      boxShadow: isDark ? 'none' : '0 2px 6px rgba(30,67,150,0.05)', flexShrink: 0,
    }}>
      {/* identity row */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
          <span style={{
            width: 10, height: 10, borderRadius: radius.full, background: liveColor,
            boxShadow: `0 0 0 3px ${withAlpha(liveColor, '33')}`,
            animation: animate ? 'rec-pulse 1.6s ease-in-out infinite' : 'none',
          }} />
          <span style={{
            fontFamily: typography.fontFamily.heading, fontSize: typography.fontSize.md,
            fontWeight: typography.fontWeight.bold, letterSpacing: typography.letterSpacing.wider, color: liveColor,
          }}>
            {jitterActive ? 'DEMO' : 'LIVE'}
          </span>
        </div>

        <div style={{ width: 1, height: 22, background: c.border, flexShrink: 0 }} />

        <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0, flex: 1 }}>
          <span style={{
            fontFamily: typography.fontFamily.heading, fontSize: typography.fontSize.xl,
            fontWeight: typography.fontWeight.bold, color: c.textPrimary,
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}>
            {deploymentName || 'Control Room'}
          </span>
          <span style={{
            fontSize: typography.fontSize.xs, color: c.textSubtle,
            letterSpacing: typography.letterSpacing.wide, textTransform: 'uppercase',
          }}>
            Operator Live Mode
          </span>
        </div>

        {freshness && <FreshnessChip freshness={freshness} animate={animate} c={c} />}

        <div style={{ width: 1, height: 22, background: c.border, flexShrink: 0 }} />

        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', flexShrink: 0 }}>
          <span style={{
            fontFamily: typography.fontFamily.heading, fontSize: typography.fontSize['2xl'],
            fontWeight: typography.fontWeight.bold, color: c.textPrimary,
            fontVariantNumeric: 'tabular-nums', lineHeight: 1.1,
          }}>
            {time}
          </span>
          <span style={{ fontSize: typography.fontSize.xs, color: c.textSubtle }}>{date}</span>
        </div>
      </div>

      {headline && <Headline headline={headline} animate={animate} c={c} />}
    </div>
  );
}

function Headline({ headline, animate, c }) {
  const showCount = headline.count > 0;
  const pulsing = animate && (headline.state === 'critical' || headline.state === 'degraded');
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 14, padding: '12px 18px', borderRadius: radius.lg,
      background: withAlpha(headline.color, '1f'), border: `1px solid ${withAlpha(headline.color, '55')}`,
      borderLeft: `5px solid ${headline.color}`,
    }}>
      <span className="material-symbols-outlined" style={{
        fontSize: 32, color: headline.color, flexShrink: 0,
        animation: pulsing ? 'rec-pulse 1.4s ease-in-out infinite' : 'none',
      }}>
        {headline.icon}
      </span>
      {showCount && (
        <span style={{
          fontFamily: typography.fontFamily.heading, fontSize: typography.fontSize['7xl'],
          fontWeight: typography.fontWeight.extrabold, color: headline.color,
          fontVariantNumeric: 'tabular-nums', lineHeight: 1,
        }}>
          {headline.count}
        </span>
      )}
      <span style={{
        fontFamily: typography.fontFamily.heading, fontSize: typography.fontSize['3xl'],
        fontWeight: typography.fontWeight.extrabold, letterSpacing: typography.letterSpacing.wide,
        color: headline.color, lineHeight: 1.05,
      }}>
        {headline.label}
      </span>
      {headline.subtext && (
        <span style={{ marginLeft: 'auto', fontSize: typography.fontSize.sm, color: c.textSubtle, fontVariantNumeric: 'tabular-nums' }}>
          {headline.subtext}
        </span>
      )}
    </div>
  );
}

function FreshnessChip({ freshness, animate, c }) {
  const { stale, ageSeconds } = freshness;
  const color = stale ? semantic.infoCyan : semantic.successAlt;
  const ageLabel = ageSeconds < 2 ? 'just now' : `${ageSeconds}s ago`;
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 7, flexShrink: 0, padding: '5px 10px',
      borderRadius: radius.full, background: c.bgSubtle, border: `1px solid ${c.border}`,
    }}>
      <span style={{
        width: 7, height: 7, borderRadius: radius.full, background: color,
        animation: animate && !stale ? 'rec-pulse 1.8s ease-in-out infinite' : 'none',
      }} />
      <span style={{ fontSize: typography.fontSize.xs, fontWeight: typography.fontWeight.bold, color, letterSpacing: typography.letterSpacing.wide }}>
        {stale ? 'DATA STALE' : 'DATA LIVE'}
      </span>
      <span style={{ fontSize: typography.fontSize.xs, color: c.textSubtle, fontVariantNumeric: 'tabular-nums' }}>
        {ageLabel}
      </span>
    </div>
  );
}
